<template>
  <div
    v-if="isOpen"
    class="shortcuts-overlay"
    role="dialog"
    aria-modal="true"
    :aria-label="t('shortcuts.title')"
    @click.self="close"
  >
    <div class="shortcuts-overlay__panel">
      <header class="shortcuts-overlay__header">
        <h2 class="shortcuts-overlay__title">{{ t('shortcuts.title') }}</h2>
        <button
          type="button"
          class="shortcuts-overlay__close"
          :aria-label="t('shortcuts.close')"
          @click="close"
        >
          <i class="fas fa-times"></i>
        </button>
      </header>

      <div class="shortcuts-overlay__groups">
        <section
          v-for="group in groups"
          :key="group.titleKey"
          class="shortcuts-overlay__group"
        >
          <h3 class="shortcuts-overlay__group-title">{{ t(group.titleKey) }}</h3>
          <ul class="shortcuts-overlay__list">
            <li
              v-for="item in group.items"
              :key="item.descKey"
              class="shortcuts-overlay__row"
            >
              <span class="shortcuts-overlay__keys">
                <template v-for="(key, i) in item.keys" :key="i">
                  <kbd class="shortcuts-overlay__key">{{ keyLabel(key) }}</kbd>
                  <span v-if="i < item.keys.length - 1" class="shortcuts-overlay__plus">+</span>
                </template>
              </span>
              <span class="shortcuts-overlay__desc">{{ t(item.descKey) }}</span>
            </li>
          </ul>
        </section>
      </div>

      <footer class="shortcuts-overlay__footer">{{ t('shortcuts.hint') }}</footer>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { isEditingText } from '@/modules/map-editor';

const { t } = useI18n();
const isOpen = ref(false);

// The shortcut handlers treat Ctrl and Cmd as equivalent (`ctrlKey || metaKey`),
// so show the modifier that matches the user's OS: Cmd on macOS, Ctrl elsewhere.
const isMac = /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent || '');

// Resolve a key token to its display label. The primary modifier is OS-aware;
// named keys are localized (Ctrl→Strg, Delete→Entf, …); letters/symbols/arrows
// (anything not listed) render literally.
function modKey() {
  return isMac ? t('shortcuts.keys.cmd') : t('shortcuts.keys.ctrl');
}
function keyLabel(token) {
  switch (token) {
    case 'mod': return modKey();
    case 'shiftOrMod': return `${t('shortcuts.keys.shift')}/${modKey()}`;
    case 'shift': return t('shortcuts.keys.shift');
    case 'enter': return t('shortcuts.keys.enter');
    case 'esc': return t('shortcuts.keys.esc');
    case 'del': return t('shortcuts.keys.del');
    case 'space': return t('shortcuts.keys.space');
    case 'drag': return t('shortcuts.keys.drag');
    case 'click': return t('shortcuts.keys.click');
    default: return token;
  }
}

// Key chords are tokens (resolved by keyLabel); descriptions are translated.
const groups = [
  {
    titleKey: 'shortcuts.groups.canvas',
    items: [
      { keys: ['del'], descKey: 'shortcuts.items.delete' },
      { keys: ['esc'], descKey: 'shortcuts.items.deselect' },
      { keys: ['mod', 'D'], descKey: 'shortcuts.items.duplicate' },
      { keys: ['mod', 'A'], descKey: 'shortcuts.items.selectAll' },
      { keys: ['↑ ↓ ← →'], descKey: 'shortcuts.items.nudge' },
      { keys: ['enter'], descKey: 'shortcuts.items.editTitle' },
      { keys: ['space', 'drag'], descKey: 'shortcuts.items.pan' },
      { keys: ['shiftOrMod', 'click'], descKey: 'shortcuts.items.extendSelection' },
      { keys: ['shift', 'drag'], descKey: 'shortcuts.items.lasso' },
    ],
  },
  {
    titleKey: 'shortcuts.groups.title',
    items: [
      { keys: ['shift', 'enter'], descKey: 'shortcuts.items.lineBreak' },
      { keys: ['mod', 'shift', 'space'], descKey: 'shortcuts.items.nbsp' },
      { keys: ['mod', '-'], descKey: 'shortcuts.items.softHyphen' },
      { keys: ['mod', 'B'], descKey: 'shortcuts.items.bold' },
      { keys: ['mod', 'I'], descKey: 'shortcuts.items.italic' },
    ],
  },
  {
    titleKey: 'shortcuts.groups.help',
    items: [
      { keys: ['?'], descKey: 'shortcuts.items.toggle' },
    ],
  },
];

function open() {
  isOpen.value = true;
}
function close() {
  isOpen.value = false;
}
function toggle() {
  isOpen.value = !isOpen.value;
}

function onKeyDown(event) {
  // Never react while typing — '?' must reach the input, not toggle the panel.
  if (isEditingText()) return;
  if (event.key === '?') {
    event.preventDefault();
    toggle();
  } else if (event.key === 'Escape' && isOpen.value) {
    event.preventDefault();
    close();
  }
}

onMounted(() => window.addEventListener('keydown', onKeyDown));
onUnmounted(() => window.removeEventListener('keydown', onKeyDown));

// Exposed for tests / programmatic control.
defineExpose({ open, close, toggle, isOpen });
</script>

<style scoped>
.shortcuts-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
}

.shortcuts-overlay__panel {
  width: min(720px, 92vw);
  max-height: 86vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
  padding: 24px 28px;
}

.shortcuts-overlay__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.shortcuts-overlay__title {
  font-size: 20px;
  font-weight: 700;
  margin: 0;
}

.shortcuts-overlay__close {
  border: none;
  background: transparent;
  font-size: 18px;
  cursor: pointer;
  color: #555;
  padding: 4px 8px;
}

.shortcuts-overlay__groups {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.shortcuts-overlay__group-title {
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #888;
  margin: 0 0 10px;
}

.shortcuts-overlay__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.shortcuts-overlay__row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 0;
}

.shortcuts-overlay__keys {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
  min-width: 170px;
}

.shortcuts-overlay__key {
  display: inline-block;
  padding: 2px 7px;
  font: 600 12px/1.4 ui-monospace, monospace;
  background: #f3f4f6;
  border: 1px solid #d6d8dc;
  border-radius: 5px;
  white-space: nowrap;
}

.shortcuts-overlay__plus {
  color: #aaa;
  font-size: 11px;
}

.shortcuts-overlay__desc {
  color: #333;
  font-size: 14px;
}

.shortcuts-overlay__footer {
  margin-top: 18px;
  color: #888;
  font-size: 12px;
  text-align: center;
}

@media (max-width: 560px) {
  .shortcuts-overlay__groups {
    grid-template-columns: 1fr;
  }
}
</style>
